import React, { useState, useEffect, useMemo } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Table,
  Modal,
  Input,
  InputNumber,
  Pagination,
  Card,
  Tooltip,
  Space,
  Button,
  Tag,
  Typography,
  Upload,
  Divider,
  Form,
  Row,
  Col,
  Empty,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  bp_get_qc_sample_queue,
  bp_get_sampling_details_by_sample,
  bp_save_sample_actual_data,
  store_sample_detail_status,
} from '@/utils/api';
import ReusableSearchInput from '@/components/ReusableSearch';
import TableContainer from '@/components/TableContainer';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CountDashboard from '@/components/CountDashbord';
import {
  CheckCheck,
  RotateCw,
  UploadCloud,
  FileText,
  FlaskConical,
  Eye,
  Calendar,
  MessageCircle,
  XCircle,
} from 'lucide-react';
import useTableConfig from '@/hooks/useTableConfig';
import TableColumnConfig from '@/components/TableColumnConfig';
import DualESignatureAuth from '@/components/DualESignatureAuth';
import { accessControlUtils } from '@/components/AccessControlUtils';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import Icons from '@/components/third-party/Icons';
import { formatDate, formatDateTime } from '@/utils/datetime';

const { Title, Text, Link } = Typography;
const { TextArea } = Input;

function SampleTesting() {
  const [sample_data, set_sample_data] = useState([]);
  const [total, set_total] = useState(0);
  const [page_number, set_page_number] = useState(1);
  const [searchvalue, setSearchvalue] = useState('');

  // QC entry form modal
  const [qc_form_open, set_qc_form_open] = useState(false);
  const [active_record, set_active_record] = useState(null);
  const [paramter_details, set_paramter_details] = useState([]);
  const [documents, set_documents] = useState([]);
  const [loading_form, set_loading_form] = useState(false);

  // Reject state inside QC modal
  const [reject_mode, set_reject_mode] = useState(false);
  const [reject_comment, set_reject_comment] = useState('');
  const [rejecting, set_rejecting] = useState(false);

  // View results modal (Completed / Rejected)
  const [view_open, set_view_open] = useState(false);
  const [view_params, set_view_params] = useState([]);
  const [view_docs, set_view_docs] = useState([]);
  const [view_record, set_view_record] = useState(null);
  const [loading_view, set_loading_view] = useState(false);

  // E-signature
  const [showESignatureModal, setShowESignatureModal] = useState(false);
  const [pendingApprovalSample, setPendingApprovalSample] = useState(null);
  const [pendingFormValues, setPendingFormValues] = useState(null);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const isESign = accessControlUtils('ESign')?.create_access || false;

  // ── API calls ──────────────────────────────────────────────────────────────

  const get_data = async () => {
    try {
      await bp_get_qc_sample_queue(
        { page_number, ...(searchvalue && { search: searchvalue }) },
        (res) => {
          set_sample_data(res?.data?.data || []);
          set_total(res?.data?.obj_count || 0);
        }
      );
    } catch (error) {
      console.error('Error fetching QC queue:', error);
    }
  };

  const open_qc_form = async (record) => {
    set_active_record(record);
    set_paramter_details([]);
    set_documents([]);
    set_reject_mode(false);
    set_reject_comment('');
    set_qc_form_open(true);
    set_loading_form(true);
    try {
      await bp_get_sampling_details_by_sample(
        (res) => {
          set_paramter_details(res?.data?.data || []);
          set_documents(res?.data?.documents || []);
        },
        { sample_detail_id: record?.sample_detail_id }
      );
    } catch (error) {
      console.error('Error fetching sample details:', error);
    } finally {
      set_loading_form(false);
    }
  };

  const open_view_modal = async (record) => {
    set_view_record(record);
    set_view_params([]);
    set_view_docs([]);
    set_view_open(true);
    set_loading_view(true);
    try {
      await bp_get_sampling_details_by_sample(
        (res) => {
          set_view_params(res?.data?.data || []);
          set_view_docs(res?.data?.documents || []);
        },
        { sample_detail_id: record?.sample_detail_id }
      );
    } catch (error) {
      console.error('Error fetching sample details:', error);
    } finally {
      set_loading_view(false);
    }
  };

  const call_status_api = async ({ record, sample_status, description, onSuccess }) => {
    try {
      await store_sample_detail_status(
        {
          sample_detail_id: record?.sample_detail_id,
          sample_number: record?.sample_number,
          batch_number: record?.batch_number,
          operation_id: record?.operation_id,
          batch_id: record?.batch_id,
          sample_status,
          ...(description ? { description } : {}),
        },
        (res) => {
          if (res?.status === 'success') {
            toast.success(res?.message || 'Status updated');
            onSuccess?.();
          } else {
            toast.warning(res?.message || 'Update failed');
          }
        }
      );
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handle_reject = async () => {
    if (!reject_comment.trim()) return;
    set_rejecting(true);
    await call_status_api({
      record: active_record,
      sample_status: false,
      description: reject_comment,
      onSuccess: () => {
        set_qc_form_open(false);
        set_reject_mode(false);
        set_reject_comment('');
        get_data();
      },
    });
    set_rejecting(false);
  };

  // Called after form save succeeds — then approve via status API
  const handle_approve_after_save = async (record) => {
    await call_status_api({
      record,
      sample_status: true,
      onSuccess: () => {
        set_qc_form_open(false);
        get_data();
      },
    });
  };

  const submit_and_approve = async (values, { setSubmitting }) => {
    const data_flow = paramter_details.map((param, i) => ({
      sampling_config_id: param.sampling_config_id,
      value: param.value !== '' ? String(param.value) : String(values.param_values[i] || ''),
      remarks: '',
    }));
    try {
      await bp_save_sample_actual_data(
        {
          sample_detail_id: active_record?.sample_detail_id,
          data_flow,
          observation: values.observation || '',
          sample_number: values.sample_number || undefined,
          quantity: values.quantity !== null ? values.quantity : undefined,
          evidence_files: values.evidence_files.map((f) => f.originFileObj).filter(Boolean),
          evidence_description: values.evidence_description || undefined,
        },
        async (res) => {
          if (res?.status === 'failed') {
            toast.warning(res?.message);
          } else {
            toast.success(res?.message || 'Sample saved');
            if (isESign) {
              setPendingApprovalSample(active_record);
              setPendingFormValues(values);
              setShowESignatureModal(true);
            } else {
              await handle_approve_after_save(active_record);
            }
          }
        }
      );
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const modifyConfirm = (record) => {
    navigate('/edit-batch', {
      state: {
        batch_number: record?.batch_number,
        recipe: record?.recipe_id,
        recipeName: record?.recipe_name,
      },
    });
    window?.localStorage?.removeItem('editRecipe', '');
  };

  // ── Columns ────────────────────────────────────────────────────────────────

  const allColumns = [
    {
      title: 'Date Received',
      dataIndex: 'date_time',
      key: 'date_time',
      defaultVisible: true,
      sortable: true,
      sortType: 'date',
      width: '12%',
      render: (val) => (val ? formatDateTime(val) : '-'),
    },
    {
      title: 'Sample Number',
      dataIndex: 'sample_number',
      key: 'sample_number',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: '12%',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Sample Number"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) =>
        record.sample_number?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Batch Number',
      dataIndex: 'batch_number',
      key: 'batch_number',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: '9%',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search Batch Number"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => record.batch_number?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'MBMR',
      dataIndex: 'recipe_name',
      key: 'recipe_name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: '11%',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search MBMR"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button type="primary" onClick={() => confirm()} size="small" style={{ width: 90 }}>
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => record.recipe_name?.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Process',
      dataIndex: 'process_name',
      key: 'process_name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: '9%',
      render: (val) => val || '-',
    },
    {
      title: 'Testing Type',
      dataIndex: 'testing_type',
      key: 'testing_type',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: '9%',
      render: (val) => val || '-',
    },
    {
      title: 'Employee',
      dataIndex: 'employee_name',
      key: 'employee_name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      width: '9%',
      render: (val) => val || '-',
    },
    {
      title: 'Machine',
      dataIndex: 'machine_name',
      key: 'machine_name',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      align: 'center',
      width: '8%',
      render: (val) => val || '-',
    },
    {
      title: 'QC',
      dataIndex: 'is_qc_sampling',
      key: 'is_qc_sampling',
      defaultVisible: true,
      align: 'center',
      width: '5%',
      render: (val) => (val ? <Tag color="gold">QC</Tag> : <Tag color="default">Inline</Tag>),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      defaultVisible: true,
      sortable: true,
      sortType: 'string',
      align: 'center',
      width: '8%',
      filters: [
        { text: 'Sent to QC', value: 'Sent to QC' },
        { text: 'Passed', value: 'Completed' },
        { text: 'Failed', value: 'Rejected' },
        { text: 'Pending', value: 'Pending' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (data) => {
        const map = {
          Completed: { label: 'Passed', color: 'success' },
          Rejected: { label: 'Failed', color: 'error' },
          'Sent to QC': { label: 'Sent to QC', color: 'processing' },
          Pending: { label: 'Pending', color: 'warning' },
        };
        const { label, color } = map[data] || { label: data, color: 'default' };
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      defaultVisible: true,
      sortable: false,
      align: 'center',
      width: '8%',
      render: (_, record) => {
        if (record?.status === 'Completed') {
          return (
            <div className="flex items-center justify-center gap-2">
              <Tooltip title="Passed">
                <CheckCheck className="w-5 h-5" style={{ color: defaultColors.success }} />
              </Tooltip>
              <Tooltip title="View Results">
                <Button
                  type="text"
                  icon={<Eye className="w-5 h-5" style={{ color: defaultColors.primary }} />}
                  onClick={() => open_view_modal(record)}
                />
              </Tooltip>
            </div>
          );
        }
        if (record?.status === 'Rejected') {
          return (
            <div className="flex items-center justify-center gap-2">
              <Tooltip title="View Comment">
                <Button
                  type="text"
                  icon={
                    <MessageCircle className="w-5 h-5" style={{ color: defaultColors.secondary }} />
                  }
                  onClick={() =>
                    Modal.info({
                      title: 'Rejection Comment',
                      content: record?.comment || 'No comment provided',
                    })
                  }
                />
              </Tooltip>
              <Tooltip title="Modify Batch">
                <Button
                  type="text"
                  icon={<Calendar className="w-5 h-5" style={{ color: defaultColors.warning }} />}
                  onClick={() => modifyConfirm(record)}
                />
              </Tooltip>
            </div>
          );
        }
        if (record?.status === 'Resampling') {
          return (
            <Tooltip title="Resampling">
              <RotateCw className="w-5 h-5" style={{ color: defaultColors.success }} />
            </Tooltip>
          );
        }
        // Sent to QC / Pending — open QC form
        return (
          <Tooltip title="Enter QC Results">
            <Button
              type="text"
              icon={<Eye className="w-5 h-5" style={{ color: defaultColors.primary }} />}
              onClick={() => open_qc_form(record)}
            />
          </Tooltip>
        );
      },
    },
  ];

  const { visibleColumns, columnConfigMenu } = useTableConfig({
    tableKey: 'sampleTestingTable',
    columns: allColumns,
    data: sample_data || [],
    searchValue: '',
  });

  const summaryData = useMemo(() => {
    const passedCount = sample_data?.filter((s) => s?.status === 'Completed')?.length || 0;
    const failedCount = sample_data?.filter((s) => s?.status === 'Rejected')?.length || 0;
    const sentToQcCount = sample_data?.filter((s) => s?.status === 'Sent to QC')?.length || 0;
    const pendingCount =
      sample_data?.filter(
        (s) => s?.status !== 'Completed' && s?.status !== 'Rejected' && s?.status !== 'Sent to QC'
      )?.length || 0;
    return {
      total_count: total || 0,
      completed_count: passedCount,
      rejected_count: failedCount,
      pending_count: pendingCount + sentToQcCount,
    };
  }, [sample_data, total]);

  const param_columns_view = [
    { title: 'Parameter', dataIndex: 'parameter_name', key: 'parameter_name', width: 160 },
    {
      title: 'Standard',
      dataIndex: 'standard',
      key: 'standard',
      width: 100,
      render: (v) => v || '-',
    },
    {
      title: 'Expected Range',
      key: 'expected_range',
      align: 'center',
      width: 140,
      render: (_, r) => `${r?.sampling_min_value ?? '-'} – ${r?.sampling_max_value ?? '-'}`,
    },
    {
      title: 'Value',
      key: 'value',
      align: 'center',
      width: 120,
      render: (_, r) => {
        if (r.value === '' || r.value === null) return <Text type="secondary">—</Text>;
        const inRange =
          Number(r.value) >= Number(r.sampling_min_value) &&
          Number(r.value) <= Number(r.sampling_max_value);
        return <Tag color={inRange ? 'success' : 'error'}>{r.value}</Tag>;
      },
    },
  ];

  const doc_list = (docs) =>
    docs?.length > 0 ? (
      <>
        <Divider orientation="left" className="mt-3 mb-2">
          <Text className="text-xs text-gray-500">Uploaded Documents</Text>
        </Divider>
        <div className="space-y-2">
          {docs.map((doc) => (
            <div
              key={doc.document_id}
              className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 border border-gray-200"
            >
              <div className="flex items-center gap-2">
                <FileText size={15} className="text-blue-500" />
                <div>
                  <Link
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm"
                  >
                    {doc.file_name}
                  </Link>
                  {doc.description && (
                    <p className="text-xs text-gray-500 m-0">{doc.description}</p>
                  )}
                </div>
              </div>
              <Text type="secondary" className="text-xs shrink-0 ml-4">
                {doc.uploaded_by} · {formatDate(doc.created_dt)}
              </Text>
            </div>
          ))}
        </div>
      </>
    ) : null;

  useEffect(() => {
    get_data();
  }, [page_number, searchvalue]);

  return (
    <div
      className="p-2.5"
      style={{ backgroundColor: isDark ? defaultColors.palette.gray[900] : '#f9fafb' }}
    >
      <Card
        className="border border-gray-300 shadow-md mt-2"
        bordered={false}
        style={{
          backgroundColor: isDark ? defaultColors.palette.gray[800] : '#ffffff',
          minHeight: 'calc(100vh - 120px)',
        }}
        title={
          <div className="flex items-center gap-2">
            <Icons.FlaskConical className="text-blue-600" size={24} />
            <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
              {t('Sample Testing')}
            </Title>
          </div>
        }
        extra={
          <div className="flex items-center gap-2">
            <TableColumnConfig menu={columnConfigMenu} />
            <ReusableSearchInput
              placeholder="Search here..."
              width={250}
              onSearch={(value) => setSearchvalue(value)}
              onChange={(e) => setSearchvalue(e.target.value)}
            />
          </div>
        }
      >
        <div style={{ marginBottom: 24 }}>
          <CountDashboard summaryData={summaryData} />
        </div>
        {/* Fixed-height, bordered table — same convention as /ticket, /process-library,
            /recipe-approval: tableLayout="fixed" + percentage column widths keep columns
            full-width and stable regardless of row count. */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            // overflow: 'visible' (not 'hidden') — 'hidden' clips the table's own
            // horizontal scrollbar at the bottom edge (same bug found on Batch360.js).
            overflow: 'visible',
          }}
        >
          <TableContainer
            size="small"
            bordered
            columns={visibleColumns}
            data={sample_data}
            rowKey="sample_detail_id"
            tableLayout="fixed"
            // Responsive: viewport-anchored so it scales on larger/smaller screens
            height="calc(100vh - 430px)"
            locale={{ emptyText: <Empty description="No samples found" /> }}
          />
        </div>
        <div className="flex justify-end mt-4">
          <Pagination
            total={total}
            showTotal={(total) => `Total ${total} samples`}
            current={page_number}
            showSizeChanger={false}
            onChange={(value) => set_page_number(value)}
          />
        </div>
      </Card>

      {/* ── QC Entry Form Modal ── */}
      <Formik
        enableReinitialize
        initialValues={{
          sample_number:
            active_record?.sample_number ||
            (active_record?.batch_number ? `${active_record.batch_number}-QC` : ''),
          quantity: null,
          observation: '',
          evidence_description: '',
          evidence_files: [],
          param_values: paramter_details.reduce((acc, _, i) => ({ ...acc, [i]: '' }), {}),
        }}
        validationSchema={Yup.object().shape({
          sample_number: Yup.string().required('Sample number is required'),
          quantity: (() => {
            let schema = Yup.number().nullable().required('Quantity is required');
            if (active_record?.sampling_min_value != null)
              schema = schema.min(
                active_record.sampling_min_value,
                `Quantity must be ≥ ${active_record.sampling_min_value}`
              );
            if (active_record?.sampling_max_value != null)
              schema = schema.max(
                active_record.sampling_max_value,
                `Quantity must be ≤ ${active_record.sampling_max_value}`
              );
            return schema;
          })(),
          observation: Yup.string().required('Observation is required'),
          param_values: Yup.lazy((obj) =>
            Yup.object(
              Object.keys(obj || {}).reduce((acc, key) => {
                const param = paramter_details[Number(key)];
                if (param && param.value === '') {
                  acc[key] = Yup.string().required('Value is required');
                }
                return acc;
              }, {})
            )
          ),
        })}
        onSubmit={submit_and_approve}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting, handleBlur }) => (
          <Modal
            open={qc_form_open}
            title={
              <div className="flex items-center gap-2">
                <FlaskConical size={18} className="text-blue-600" />
                <span>QC Results — {active_record?.batch_number}</span>
                {active_record?.status && (
                  <Tag color="processing" className="ml-1">
                    {active_record.status}
                  </Tag>
                )}
              </div>
            }
            width={1020}
            centered
            className="qc-results-modal"
            onCancel={() => {
              set_qc_form_open(false);
              set_reject_mode(false);
              set_reject_comment('');
            }}
            destroyOnClose
            styles={{ body: { maxHeight: 640, overflowY: 'auto', overflowX: 'hidden' } }}
            footer={
              <div className="flex items-start gap-2">
                {reject_mode ? (
                  <div className="flex-1 flex flex-col gap-2">
                    <TextArea
                      rows={2}
                      placeholder="Enter rejection reason (required)"
                      value={reject_comment}
                      onChange={(e) => set_reject_comment(e.target.value)}
                      autoFocus
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => {
                          set_reject_mode(false);
                          set_reject_comment('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        danger
                        loading={rejecting}
                        disabled={!reject_comment.trim()}
                        icon={<XCircle size={15} />}
                        onClick={handle_reject}
                      >
                        Confirm Reject
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        set_qc_form_open(false);
                        set_reject_comment('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      danger
                      icon={<XCircle size={15} />}
                      onClick={() => set_reject_mode(true)}
                    >
                      Reject
                    </Button>
                    <Button
                      type="primary"
                      loading={isSubmitting}
                      style={{
                        backgroundColor: defaultColors.primary,
                        borderColor: defaultColors.primary,
                      }}
                      onClick={handleSubmit}
                    >
                      Approve &amp; Save
                    </Button>
                  </>
                )}
              </div>
            }
          >
            {/* Thin scrollbar on the scrollable body — same treatment as TableContainer. */}
            <style>{`
              .qc-results-modal .ant-modal-body {
                scrollbar-width: thin;
                scrollbar-color: ${isDark ? '#64748b' : '#94a3b8'} transparent;
              }
              .qc-results-modal .ant-modal-body::-webkit-scrollbar {
                width: 6px;
              }
              .qc-results-modal .ant-modal-body::-webkit-scrollbar-track {
                background: transparent;
              }
              .qc-results-modal .ant-modal-body::-webkit-scrollbar-thumb {
                background: ${isDark ? '#64748b' : '#94a3b8'};
                border-radius: 4px;
              }
              .qc-results-modal .ant-modal-body::-webkit-scrollbar-thumb:hover {
                background: ${isDark ? '#94a3b8' : '#64748b'};
              }
            `}</style>
            {/* Info strip */}
            <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
              <div>
                <Text type="secondary" className="text-xs block">
                  MBMR
                </Text>
                <Text strong>{active_record?.recipe_name || '-'}</Text>
              </div>
              <div>
                <Text type="secondary" className="text-xs block">
                  Process
                </Text>
                <Text strong>{active_record?.process_name || '-'}</Text>
              </div>
              <div>
                <Text type="secondary" className="text-xs block">
                  Testing Type
                </Text>
                <Text strong>{active_record?.testing_type || '-'}</Text>
              </div>
            </div>

            {/* QC Quantity Range */}
            {(active_record?.sampling_min_value != null ||
              active_record?.sampling_max_value != null) && (
              <div className="mb-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <Text className="text-xs text-yellow-700 font-medium block mb-2">
                  QC Quantity Range
                </Text>
                <Row gutter={16}>
                  <Col span={12}>
                    <Text type="secondary" className="text-xs block">
                      Min Quantity
                    </Text>
                    <Text strong>{active_record?.sampling_min_value ?? '-'}</Text>
                  </Col>
                  <Col span={12}>
                    <Text type="secondary" className="text-xs block">
                      Max Quantity
                    </Text>
                    <Text strong>{active_record?.sampling_max_value ?? '-'}</Text>
                  </Col>
                </Row>
              </div>
            )}

            {/* Conditional Parameter */}
            {active_record?.conditional_parameter && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Text className="text-xs text-blue-600 font-medium block mb-2">
                  Conditional Parameter
                </Text>
                <Row gutter={16}>
                  <Col span={8}>
                    <Text type="secondary" className="text-xs block">
                      Parameter
                    </Text>
                    <Text strong>{active_record.conditional_parameter.parameter_name}</Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" className="text-xs block">
                      Min Value
                    </Text>
                    <Text strong>
                      {active_record.conditional_parameter.min_value ?? '-'}
                      {active_record.conditional_parameter.unit && (
                        <span className="ml-1 text-gray-500 text-xs">
                          {active_record.conditional_parameter.unit}
                        </span>
                      )}
                    </Text>
                  </Col>
                  <Col span={8}>
                    <Text type="secondary" className="text-xs block">
                      Max Value
                    </Text>
                    <Text strong>
                      {active_record.conditional_parameter.max_value ?? '-'}
                      {active_record.conditional_parameter.unit && (
                        <span className="ml-1 text-gray-500 text-xs">
                          {active_record.conditional_parameter.unit}
                        </span>
                      )}
                    </Text>
                  </Col>
                </Row>
              </div>
            )}

            <Form layout="vertical" onFinish={handleSubmit}>
              <Row gutter={16}>
                {/* Sample Number */}
                <Col span={12}>
                  <Form.Item
                    label="Sample Number"
                    required
                    validateStatus={touched.sample_number && errors.sample_number ? 'error' : ''}
                    help={touched.sample_number && errors.sample_number ? errors.sample_number : undefined}
                  >
                    <Input
                      placeholder="QC-assigned sample number"
                      value={values.sample_number}
                      onChange={(e) => setFieldValue('sample_number', e.target.value)}
                      onBlur={() => handleBlur({ target: { name: 'sample_number' } })}
                    />
                  </Form.Item>
                </Col>

                {/* Quantity */}
                <Col span={12}>
                  <Form.Item
                    label="Quantity"
                    required
                    validateStatus={touched.quantity && errors.quantity ? 'error' : ''}
                    help={
                      touched.quantity && errors.quantity
                        ? errors.quantity
                        : active_record?.sampling_min_value != null ||
                          active_record?.sampling_max_value != null
                        ? `Range: ${active_record?.sampling_min_value ?? '-'} – ${
                            active_record?.sampling_max_value ?? '-'
                          }`
                        : undefined
                    }
                  >
                    <InputNumber
                      className="w-full"
                      placeholder="Actual quantity"
                      min={active_record?.sampling_min_value ?? 0}
                      max={active_record?.sampling_max_value ?? undefined}
                      value={values.quantity}
                      onChange={(val) => setFieldValue('quantity', val)}
                      onBlur={() => handleBlur({ target: { name: 'quantity' } })}
                    />
                  </Form.Item>
                </Col>

                {/* Dynamic parameter inputs */}
                {paramter_details.map((param, index) => (
                  <Col span={12} key={param.sampling_config_id ?? index}>
                    <Form.Item
                      label={
                        <span>
                          {param.parameter_name}
                          {param.standard && (
                            <Tag color="default" className="ml-2 text-xs">
                              {param.standard}
                            </Tag>
                          )}
                          <span className="ml-2 text-gray-400 text-xs font-normal">
                            ({param.sampling_min_value ?? '-'} – {param.sampling_max_value ?? '-'})
                          </span>
                        </span>
                      }
                      validateStatus={
                        touched.param_values?.[index] && errors.param_values?.[index] ? 'error' : ''
                      }
                      help={touched.param_values?.[index] && errors.param_values?.[index]}
                    >
                      {param.value !== '' ? (
                        <Tag
                          color={
                            Number(param.value) >= Number(param.sampling_min_value) &&
                            Number(param.value) <= Number(param.sampling_max_value)
                              ? 'success'
                              : 'error'
                          }
                          className="text-sm px-3 py-1"
                        >
                          {param.value}
                        </Tag>
                      ) : (
                        <InputNumber
                          className="w-full"
                          placeholder="Enter value"
                          value={values.param_values[index]}
                          onChange={(val) =>
                            setFieldValue('param_values', { ...values.param_values, [index]: val })
                          }
                        />
                      )}
                    </Form.Item>
                  </Col>
                ))}

                {/* Observation */}
                <Col span={24}>
                  <Form.Item
                    label="Observation"
                    required
                    validateStatus={touched.observation && errors.observation ? 'error' : ''}
                    help={touched.observation && errors.observation ? errors.observation : undefined}
                  >
                    <Input.TextArea
                      rows={2}
                      placeholder="Free-text observation"
                      value={values.observation}
                      onChange={(e) => setFieldValue('observation', e.target.value)}
                      onBlur={() => handleBlur({ target: { name: 'observation' } })}
                    />
                  </Form.Item>
                </Col>

                {/* Evidence description */}
                <Col span={24}>
                  <Form.Item label="Evidence Description">
                    <Input
                      placeholder="Description for uploaded files"
                      value={values.evidence_description}
                      onChange={(e) => setFieldValue('evidence_description', e.target.value)}
                    />
                  </Form.Item>
                </Col>

                {/* Evidence upload */}
                <Col span={24}>
                  <Form.Item label="Evidence Files">
                    <Upload
                      multiple
                      beforeUpload={() => false}
                      fileList={values.evidence_files}
                      onChange={({ fileList }) => setFieldValue('evidence_files', fileList)}
                      accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                    >
                      <Button icon={<UploadCloud size={15} />}>Attach Files</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                {/* Existing documents */}
                {documents?.length > 0 && <Col span={24}>{doc_list(documents)}</Col>}
              </Row>
            </Form>
          </Modal>
        )}
      </Formik>

      {/* ── View Results Modal (Completed / Rejected) ── */}
      <Modal
        open={view_open}
        title={
          <div className="flex items-center gap-2">
            <Eye size={18} className="text-blue-600" />
            <span>View Results — {view_record?.sample_number}</span>
            {view_record?.status && (
              <Tag
                color={view_record.status === 'Completed' ? 'success' : 'error'}
                className="ml-1"
              >
                {view_record.status === 'Completed' ? 'Passed' : 'Failed'}
              </Tag>
            )}
          </div>
        }
        width={700}
        footer={null}
        onCancel={() => set_view_open(false)}
        destroyOnClose
      >
        <div className="grid grid-cols-3 gap-3 mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
          <div>
            <Text type="secondary" className="text-xs block">
              Batch
            </Text>
            <Text strong>{view_record?.batch_number || '-'}</Text>
          </div>
          <div>
            <Text type="secondary" className="text-xs block">
              MBMR
            </Text>
            <Text strong>{view_record?.recipe_name || '-'}</Text>
          </div>
          <div>
            <Text type="secondary" className="text-xs block">
              Testing Type
            </Text>
            <Text strong>{view_record?.testing_type || '-'}</Text>
          </div>
        </div>

        <Table
          size="small"
          bordered
          pagination={false}
          loading={loading_view}
          rowKey="sampling_config_id"
          columns={param_columns_view}
          dataSource={view_params}
        />

        {doc_list(view_docs)}
      </Modal>

      {/* ── E-Signature ── */}
      <DualESignatureAuth
        visible={showESignatureModal}
        onCancel={() => {
          setShowESignatureModal(false);
          setPendingApprovalSample(null);
          setPendingFormValues(null);
        }}
        onSubmit={async () => {
          await handle_approve_after_save(pendingApprovalSample);
          setShowESignatureModal(false);
          setPendingApprovalSample(null);
          setPendingFormValues(null);
        }}
        signatureType="sample_approval"
        action_type="sample_approval"
        signatureMeaning="Sample Approval"
        documentType="SAMPLE"
        sample_number={pendingApprovalSample?.sample_number || ''}
        documentId={pendingApprovalSample?.sample_detail_id || ''}
        documentNumber={pendingApprovalSample?.sample_number || ''}
        documentData={{
          sample_detail_id: pendingApprovalSample?.sample_detail_id || '',
          sample_number: pendingApprovalSample?.sample_number || '',
          batch_number: pendingApprovalSample?.batch_number || '',
          operation_id: pendingApprovalSample?.operation_id || '',
          batch_id: pendingApprovalSample?.batch_id || '',
          status: pendingApprovalSample?.status || '',
        }}
        config={{
          enableBasic: true,
          enableCFR: true,
          defaultMode: 'cfr',
          requireComment: true,
          title: 'E-Signature for Sample Approval',
        }}
      />
    </div>
  );
}

export default SampleTesting;
